import { 
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useCallback, 
  useEffect, 
  useRef,
  RefCallback,
  forwardRef
 } from 'react';
import './Modal.css';
import useOnOutsideClick from '../../../hooks/useOnOutsideClick';
import PortalModal from './PortalModal';

//#region Modal Component
enum ModalPositionX {
  center = "center",
  right = "right",
  left = "left",
}

enum ModalPositionY {
  center = "center",
  start = "start",
  end = "end",
}

interface ModalConfig {
  title: string;
  showHeader: boolean;
  positionX: ModalPositionX;
  positionY: ModalPositionY;
  padding?: string;
  showOverlay: boolean;
}

interface ModalProps {
  show: boolean;
  config: ModalConfig;
  setShow: (value: boolean) => void;
  resetPdfSrc: () => void;
  children: JSX.Element | JSX.Element[];
  inputRef?: RefCallback<HTMLDivElement>;
}

function Modal(props: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  if(props.inputRef && modalRef) {
    props.inputRef(modalRef.current);
  }

  const onClickOutsideHandler = () => {
    props.setShow(false);
    props.resetPdfSrc(); // Reset the PDF source
  }

  const onKeyPressHandler = useCallback((evt: KeyboardEvent) => {
    if (evt.key === "Escape") {
      props.setShow(false);
    }
  }, [props]);

  useOnOutsideClick(modalRef, onClickOutsideHandler);

  useEffect(() => {
    if (props.show) {
      document.addEventListener("keydown", onKeyPressHandler);
    }
    return () => document.removeEventListener("keydown", onKeyPressHandler);
  }, [onKeyPressHandler, props.show]);

  const closeModal = () => {
    props.resetPdfSrc(); // Reset the PDF source
    props.setShow(false); // Close the modal
  };

  return (
    <>
      {props.show && (
        <PortalModal wrapperId='geoconnect-modal-portal'>
          <div className='modal-overlay'
            style={{
              backgroundColor: props.config.showOverlay ? "rgba(23, 23, 23, 0.8)" : "rgba(0, 0, 0, 0)",
              alignItems: props.config.positionX ? props.config.positionX : "center",
              justifyContent: props.config.positionY ? props.config.positionY : "center"
            }}
          >
            <div className="modal-container"
              style={{ padding: props.config.padding ? props.config.padding : "20px" }}
              ref={modalRef}>
              {props.config.showHeader && (
                <div className="modal-header">
                  <h3>{props.config.title}</h3>
                </div>
              )}
              <button className='modal-close-button' onClick={closeModal}>X</button>
              <>{props.children}</>
            </div>
          </div>
        </PortalModal>
      )}
    </>
  )
}
//#endregion


//#region Modal Content Component
interface ModalContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  width?: number | string | undefined;
  height?: number | string | undefined;
  maxHeight?: number;
  style?: CSSProperties | undefined;
};


const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>((props, ref) => {
  const modalContentStyles: CSSProperties = {
    ...props.style, 
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  if(props.maxHeight) {
    modalContentStyles.maxHeight = `${props.maxHeight - 40 - 35 }px`;
  }
  if (props.height) {
    modalContentStyles.height = `${props.height}`;
  }
  if (props.width) {
    modalContentStyles.width = `${props.width}`;
  }

  return (
    <div ref={ref} style={modalContentStyles}>{props.children}</div>
  )
  });
//#endregion

//#region Modal Footer Component
interface ModalFooterProps {
  children: JSX.Element | JSX.Element[];
}

function ModalFooter( {children}: ModalFooterProps) {
  return (
    <footer className='modal-footer'>
      {children}
    </footer>
  )
}
//#endregion

//#region Button Component
interface ModalButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  icon?: string;
}

// const modalButton: CSSProperties = {
//   display: 'block',
// 	transition: '0.3s ease all',
//   margin: '1rem 0.5rem',
// };

function PrimaryButton({title, ...props}: ModalButtonProps) {
  return (
    <button 
      type='button' 
      {...props} 
      // style={modalButton}
    >
      {props.icon && <i className={props.icon}/>}
      {title}
    </button>
  );
}

function SecondaryButton({title, ...props}: ModalButtonProps) {
  return (
    <button 
      type='button' 
      {...props} 
      // style={modalButton} 
      className='bg-orange no-border'>
        {props.icon && <i className={props.icon}/>}
        {title}
      </button>
  );
}
//#endregion

export {
  ModalPositionX,
  ModalPositionY,
  type ModalConfig,
  Modal,
  ModalContent,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
}